/**
 * The agent websocket protocol, typed. The backend's `manage.py agent_protocol --json` is the
 * source of truth; `protocol.fixture.json` is its committed output, and `protocol.test.ts` checks
 * every table here against it, so a backend change that the frontend has not followed fails a test.
 */
import type { SandboxErrorData } from "../sandbox/core/types";
import type { Candidate, SandboxClientCall, SandboxHandIn } from "../sandbox/core/hostCalls";

export const PROTOCOL_VERSION = 1;

/** "application:12": a context type and id, the same string on every frame. */
export type ContextRef = `${string}:${number}`;

/** A page's on-screen state sent with a message: plain JSON per slot, which the server validates. */
export interface PageState {
  context_ref: ContextRef;
  slots: Record<string, Record<string, unknown>>;
}

export type RunStatus =
  | "queued"
  | "running"
  | "awaiting_choice"
  | "awaiting_event"
  | "completed"
  | "failed"
  | "cancelled";
export type ToolOutcome = "success" | "failed" | "denied" | "retry";
export type ChoiceKind = "approval" | "pick" | "filing";
export type ChoiceStatus = "pending" | "decided" | "expired" | "cancelled";
/** `apps.agentic.core.enums.ToolUiKind`: how surfaces draw a call; picks a native card. */
export type ToolUiKind =
  | "lookup"
  | "action"
  | "decision"
  | "merchant_tasks"
  | "application_decision"
  | "offer"
  | "refinance"
  | "merchant_flow"
  | "new_application";

/** The tool's own line per state for a call (from its approval metadata or stored result). */
export type ActivityLines = Partial<
  Record<"running" | "awaiting_approval" | "succeeded" | "failed", string>
>;
/** What a call shows: the tool's lines per state, one line, or null (a generic phrase). */
export type ToolActivity = ActivityLines | string | null;

export type WirePart =
  | { kind: "text"; text: string }
  | { kind: "reasoning"; text: string }
  | {
      kind: "tool_call";
      id: string;
      /** Internal; never shown to users. */
      name: string;
      args: Record<string, unknown>;
      activity: string | null;
      /** The tool's ToolUi kind; optional, so a client reads a part from an older sender too. */
      ui_kind?: ToolUiKind | null;
    }
  | {
      kind: "tool_result";
      id: string;
      name: string;
      outcome: ToolOutcome;
      content: unknown;
      /** The call's lines once it ran: one per state, or a single line. */
      activity?: ToolActivity;
      /**
       * What the call did as its native card draws it (`Tool.result_card`), with a `kind`; a
       * code-mode repl's is its sandbox_cell.
       */
      card?: unknown;
    }
  /** `name` is the uploader's own, else a neutral label from the server. `pages` are the PDF
   * pages sent ("2-5"), null for the whole file. */
  | {
      kind: "file";
      thread_file_id: number | null;
      media_type: string;
      name: string;
      pages: string | null;
    }
  /** A voice note the user sent on a messaging channel (a video note too): its sound, kept as a
   * thread file (MP3), and what it says. The model reads only the transcript. */
  | {
      kind: "voice";
      thread_file_id: number;
      media_type: string;
      name: string;
      seconds: number;
      transcript: string;
    }
  /** A chart a tool showed, in a tool message after its call's result (drawn by the chart card). */
  | {
      kind: "chart";
      title: string;
      /** A Vega-Lite spec, data inlined, no theme. */
      vegalite: Record<string, unknown>;
      table: WireChartTable;
      source_refs: { application_id: number | null; analysis_run_id: number | null };
      /** When the data was computed (ISO 8601); copied when the chart was made. */
      as_of: string | null;
    }
  /** A /workspace file the assistant shows the user (declared; PF1 writes it). */
  | { kind: "preview"; workspace: string; path: string }
  /** A code cell the assistant ran: its line, never its code (declared; CM3/CM4 write it). */
  | {
      kind: "sandbox_cell";
      language: "js" | "python" | "bash";
      summary: string;
      calls: { tool: string; outcome: string }[];
      approvals: string[];
      duration_ms: number;
    };

/** A chart's values: the data table, the CSV download. Numbers are unformatted. */
export interface WireChartTable {
  columns: { key: string; label: string; type: string; unit?: string }[];
  rows: Record<string, string | number | boolean | null>[];
}

export interface WireMessage {
  id: number;
  seq: number;
  /** "event": a line the server wrote (an event the assistant waited for happened). "boundary":
   * where the user cleared the conversation (/clear); the assistant reads nothing before it. */
  role: "user" | "assistant" | "tool" | "event" | "boundary";
  created_at: string;
  /** The display name of the model that wrote an assistant message; null elsewhere or unknown. */
  model?: string | null;
  parts: WirePart[];
}

/** A chat command: a message whose whole text is `name` runs it instead of going to the model. */
export interface WireCommand {
  name: string;
  summary: string;
}

export interface WireChoiceOption {
  id: string;
  label: string;
  style: string;
  /** An approve variant's (`_variant_options`): the arguments pressing it changes. */
  overrides?: Record<string, unknown>;
}

/** One row of an approval card's key/value grid; `group` is the section it sits under and `old`
 * the value it replaces (the card reads "old → new"). A list card's rows also have a `key`: they
 * can be ticked (`ticked`, as proposed), bill `cost` and their `fee` once, or can't run
 * (`disabled`, why); once approved a ticked row has its `state` and the `line` its run reported. */
export interface ChoiceField {
  label: string;
  value: string;
  group?: string;
  old?: string;
  key?: string;
  ticked?: boolean;
  cost?: string;
  fee?: string;
  disabled?: string;
  state?: "running" | "done" | "failed" | "skipped";
  line?: string;
}

/** A list card's approve label as rows are ticked: "Run 2 searches". */
export interface TickLabel {
  verb: string;
  one: string;
  many: string;
}

/** A choice's display hints: its call's line(s) and its card's grid (see `choice.ts`). */
/** A before-and-after figure; money as "1234.56", percents as "18.2", "" when there is none. */
export interface BlockFigure {
  label: string;
  old: string;
  new: string;
}

export interface BlockChange {
  old: string;
  new: string;
}

/** A transaction as the AI Analysis tab's table shows it, under its list card row's `key`. */
export interface BlockTransaction {
  key: string;
  override_key: string;
  account_last4: string;
  date: string;
  amount: string;
  /** The running balance after it, when the statement printed one. */
  balance?: string;
  /** Identical transactions the row stands for (a correction sets them all); 1 when unique. */
  count: number;
  description: string;
  memo: string;
  counterparty: string;
  category: BlockChange;
  revenue: BlockChange;
}

export type PositionField =
  | "include_in_leverage"
  | "frequency"
  | "status"
  | "funder_name"
  | "payment_amount";

/** One field a position card changes: a list card row (`key`), shown as text, and its new value. */
export interface BlockPositionRow {
  key: string;
  field: PositionField;
  label: string;
  old: string;
  new: string;
  to: string | boolean;
  /** A field no screen sets (funder, payment, status). */
  extended: boolean;
}

/** A position as the dashboard's funder card shows it. */
export interface BlockPositionSide {
  funder: string;
  amount: string;
  frequency: string;
  status: string;
  in_leverage: boolean;
  /** A correction's (or the detector's) explicit in or out, which outranks the default rule. */
  leverage_override: "in" | "out" | "";
  /** The leverage rule's cadence test on the stored frequency (a legacy cadence can pass it). */
  scheduled: boolean;
  payments: number;
  funded: string;
  funding_date: string;
}

/** A position an approval changes: its field rows, before and after, and its transactions. */
export interface BlockPosition {
  key: string;
  funder: string;
  /** Its fields' heading ("since Mon YYYY" when two share one); "" on an older card. */
  heading?: string;
  kind: "mca" | "loc" | "loan";
  rows: BlockPositionRow[];
  now: BlockPositionSide;
  after: BlockPositionSide;
  /** Newest first; some of them when there are many (`counts.transactions` is all). */
  transactions: BlockTransaction[];
  counts: { transactions: number; payments: number; fundings: number; returns: number };
}

/** An approval card's typed blocks: the run it is about, the top-row figures and the transaction table. */
export interface ChoiceBlocks {
  analysis?: { application_id: number; run_id: number; source: "statements" | "plaid" };
  stats?: {
    recent_revenue: BlockFigure[];
    average_revenue?: BlockFigure;
    leverage?: BlockFigure & { target: string };
    mca_payout?: BlockFigure;
    offer?: BlockFigure;
    /** The funding decision's status ("approved", "declined", "needs_review"; "" for none). */
    decision?: BlockFigure;
    /** The heading of the fields that say the same, which a renderer of the tiles leaves out. */
    group?: string;
    /** "proposed": a card approved for some of its rows whose figures are still the proposal's
     * (they couldn't be worked out again for those rows). */
    scope?: "proposed";
  };
  transactions?: { rows: BlockTransaction[] };
  positions?: BlockPosition[];
}

export interface WireChoiceDisplay {
  activity?: ActivityLines | string;
  fields?: ChoiceField[];
}

/** A tool's own approval card: its `kind` and `version`, then the fields that kind defines. */
export interface WireChoiceCard {
  kind: string;
  version: number;
  [field: string]: unknown;
}

export interface WireChoice {
  ref: string;
  kind: ChoiceKind;
  thread_id: number | null;
  run_id: number | null;
  tool_call_id: string | null;
  tool: string;
  prompt: string;
  summary: string;
  options: WireChoiceOption[];
  items: unknown[];
  status: ChoiceStatus;
  chosen: string | null;
  cancel_reason: string | null;
  expires_at: string | null;
  // G1's fields: optional, so a client reads a choice from an older sender too.
  /** An approval card's rows; empty when the tool gave none. */
  fields?: ChoiceField[];
  /** The call's line per state; empty when the tool gave none. */
  activity?: ActivityLines;
  /** A list card's fees, `{fee: "4.00"}`, each billed once however many of its rows are ticked. */
  fees?: Record<string, string>;
  /** A list card's approve label for the rows ticked; null without one. */
  tick_label?: TickLabel | null;
  /** What an approval card shows beside its fields, as the server cleaned it (apps/agentic/tools/blocks.py). */
  blocks?: ChoiceBlocks | null;
  /** The tool's own approve-button label (already on the approve option); null for the default. */
  approve_label?: string | null;
  /** The tool's own card (plain JSON; `kind` names it, e.g. "merchant_flow"); null for the generic one. */
  card?: WireChoiceCard | null;
  /** The stored call the card shows; a press with changes echoes it as `shown`. */
  args_sha256?: string | null;
  /** The approved call has run; a decision alone is not a result. */
  executed?: boolean;
  /** Why the approved call failed, when it did (then not `executed`); null otherwise. */
  error?: string | null;
  decided_via?: "web" | "whatsapp" | "telegram" | "mcp" | null;
  decided_at?: string | null;
  /** The repl call's tool_call_id when a code cell opened this card; null otherwise. */
  in_call?: string | null;
  /** @deprecated The pre-G1 place for `fields` and `activity`; read only as a fallback. */
  display?: WireChoiceDisplay;
}

export interface RunHead {
  id: number;
  status: RunStatus;
  error: string | null;
  /**
   * An awaiting run's calls that wait for an event, with their waiting lines; `client` is what the
   * browser runs for one (a sandbox call), answered with `sandbox.result`.
   */
  waiting?: { id: string; label: string; client?: SandboxClientCall }[];
}

export type DeviceKind = "desktop" | "mobile";
/** Where a tab shows the chat: the in-page card or the /assistant page. */
export type TabView = "card" | "assistant";

export interface PresenceTab {
  tab_id: string;
  device_id: string;
  colour: number;
  thread_id: number | null;
  context_ref: ContextRef | null;
  focused: boolean;
  caret: number | null;
  selection: [number, number] | null;
  device_kind: DeviceKind | null;
  view: TabView | null;
  last_active: number;
  idle: boolean;
}

export type OutgoingPart =
  | { kind: "text"; text: string }
  /** `pages`: a PDF's pages for the model to see ("3", "2-5", "1,4-6"); leave out for the file. */
  | { kind: "file"; thread_file: number; pages?: string };

/** Every command and its fields (the envelope's `v`, `type` and `cid` are added on send). */
export interface Commands {
  /** `executor`: this tab's sandbox candidate report, or null (the host election reads it). */
  hello: {
    ticket: string;
    tab_id: string;
    device_id: string;
    device_kind?: DeviceKind | null;
    executor?: Candidate | null;
  };
  subscribe: { thread?: number | null; context_ref?: ContextRef | null };
  unsubscribe: { thread: number };
  "message.send": {
    thread: number;
    cid: string;
    parts: OutgoingPart[];
    model_ref?: string | null;
    /** Only when sent from the page of the thread's context: what that page shows. */
    page_state?: PageState | null;
  };
  "run.cancel": { run: number };
  /** `items` (a filing confirm only): the files left out, and the user's types for others.
   * `overrides` (an approval's approve only): what the user changed on its card, in the shape
   * its card's kind defines. `shown`, required with them and sent on every approve: the choice's
   * `args_sha256` as the card showed it; a press on a card redrawn since is refused unapplied.
   * `rows` (a list card's approve only): the keys of the rows the user ticked; a press sends rows
   * or overrides. */
  "choice.decide": {
    ref: string;
    option: string;
    items?: { thread_file_id: number; doc_type?: string; include?: false }[] | null;
    overrides?: Record<string, unknown> | null;
    shown?: string | null;
    rows?: string[] | null;
  };
  /** Text, files (this thread's file ids), or both; a field left out keeps its value. */
  "composer.update": { thread: number; text?: string; files?: number[] };
  "presence.update": {
    thread?: number | null;
    caret?: number | null;
    selection?: [number, number] | null;
    active?: boolean;
    device_kind?: DeviceKind | null;
    view?: TabView | null;
    /** The candidate report again when it changed (debounced 5 s); null clears it, left out keeps it. */
    executor?: Candidate | null;
  };
  "focus.set": {
    focused: boolean;
    context_ref?: ContextRef | null;
    page_state?: PageState | null;
  };
  "tab.close": { tab_id: string };
  /** A sandbox call's answer, from the tab holding its workspace lease; acked `{fired}`. */
  "sandbox.result": SandboxHandIn;
  /** Code mode (CM3): acked `{queued: true}`, then answered to this tab with sandbox.tool_result; refused `unavailable` where code mode is off. */
  "sandbox.tool_call": { call: string; seq: number; name: string; args: Record<string, unknown> };
  "sandbox.wait": { call: string; seq: number; choices: string[]; timeout_ms: number | null };
  /** Declared; refused `unavailable` until PF2: a viewer's read of a live workspace file. */
  "sandbox.fs": {
    req: string;
    workspace: string;
    op: SandboxFsOp;
    path: string;
    offset?: number | null;
    limit?: number | null;
  };
  "sandbox.fs_answer": {
    req: string;
    result?: Record<string, unknown> | null;
    error?: SandboxErrorData | null;
  };
  ping: Record<string, never>;
}
export type CommandType = keyof Commands;

interface Durable {
  thread_id: number;
  rev: number;
}
interface Ephemeral {
  thread_id: number;
  run_id: number;
}

/** A file on a draft: one of the thread's files, finished uploading. */
export interface WireDraftFile {
  id: number;
  name: string;
  content_type: string;
}

export interface Frames {
  "hello.ok": {
    tab_id: string;
    device_id: string;
    colour: number;
    focus: ContextRef | null;
    idle_after: number;
    ping_interval: number;
    /** The chat commands a message may be (backend core/commands.py), for the composer's menu. */
    commands: WireCommand[];
  };
  snapshot: Durable & {
    profile: string;
    channel: string;
    context_ref: ContextRef | null;
    model_ref: string | null;
    messages: WireMessage[];
    has_earlier: boolean;
    pending_seqs: number[];
    run: RunHead | null;
    open_choices: WireChoice[];
    /** The messages' calls' choices in any state, plus the latest decided filing choice (G1). */
    choices?: WireChoice[];
    /** A bound thread's: the user's waiting approvals of calls outside any thread about its context. */
    context_choices?: WireChoice[];
    composer: { rev: number; text: string; files?: WireDraftFile[] };
  };
  "message.appended": Durable & { message: WireMessage };
  "run.status": Durable & { run: RunHead; input_seqs?: number[] };
  "choice.requested": Durable & { choice: WireChoice };
  "choice.resolved": Durable & { choice: WireChoice };
  "thread.updated": Durable & {
    thread: { context_ref: ContextRef | null; model_ref: string | null };
  };
  "message.delta": Ephemeral & {
    index: number;
    offset: number;
    part_kind: "text" | "reasoning";
    content: string;
  };
  "tool.call": Ephemeral & { id: string; name: string; activity: string | null };
  "tool.result": Ephemeral & {
    id: string;
    name: string;
    outcome: ToolOutcome;
    activity: ToolActivity;
  };
  /** The whole draft after a write; missing files means none. */
  "composer.patch": {
    thread_id: number;
    rev: number;
    text: string;
    files?: WireDraftFile[];
    tab_id: string | null;
  };
  presence: { tabs: PresenceTab[] };
  "focus.changed": { device_id: string; context_ref: ContextRef; tab_id: string };
  "threads.changed": { thread_id: number };
  "context.changed": { context_ref: ContextRef };
  /** An approval of a call outside any thread about `context_ref` changed (to the user's sockets). */
  "choice.updated": { context_ref: ContextRef; choice: WireChoice };
  /** Declared by K0; sent once their owners (ER2, ER3, CM3, PF2, WU1) build them. User-level, no rev. */
  "sandbox.host": {
    role: "host" | "standby";
    kind: "on_device" | "hosted";
    epoch: number;
    /** With kind hosted: a hosted executor serves the calls, so every tab stands down. */
    served?: boolean;
  };
  "sandbox.waiting": { calls: SandboxClientCall[] };
  "sandbox.tool_result": {
    call: string;
    seq: number;
    status: "ok" | "approval_required" | "denied" | "error";
    /** The tool's result on ok; a wait's `{<choice id>: approved | denied | pending}`. */
    result?: unknown;
    choice?: string;
    /** retryable: `running`, the same seq is still being answered; send it again shortly. */
    error?: { code: string; message: string; outcome: string; retryable?: true };
  };
  "sandbox.fs_result": {
    req: string;
    result: Record<string, unknown> | null;
    error: SandboxErrorData | null;
  };
  "sandbox.fs_request": {
    req: string;
    workspace: string;
    op: SandboxFsOp;
    path: string;
    offset: number | null;
    limit: number | null;
  };
  "workspace.changed": { workspace: string; index_rev: number; size_bytes: number };
  ack: { command: string; result: Record<string, unknown> };
  error: {
    command: string | null;
    code: ErrorCode;
    message: string;
    fields: Record<string, unknown>;
    /** A transient refusal (a code call that could not be queued): send it again with backoff. */
    retryable?: true;
  };
  pong: Record<string, never>;
}
export type FrameType = keyof Frames;
export type SandboxFsOp = "read" | "list" | "stat";
export type Frame = { [K in FrameType]: { type: K; cid?: string } & Frames[K] }[FrameType];
export type FrameOf<K extends FrameType> = Extract<Frame, { type: K }>;

/** Frames that carry a thread's `rev` and go through the rev rule. */
export const DURABLE_FRAMES = [
  "message.appended",
  "run.status",
  "choice.requested",
  "choice.resolved",
  "thread.updated",
] as const satisfies readonly FrameType[];
export type DurableFrame = FrameOf<(typeof DURABLE_FRAMES)[number]>;

export const COMMAND_FIELDS = {
  hello: ["ticket", "tab_id", "device_id", "device_kind", "executor"],
  subscribe: ["thread", "context_ref"],
  unsubscribe: ["thread"],
  "message.send": ["thread", "cid", "parts", "model_ref", "page_state"],
  "run.cancel": ["run"],
  "choice.decide": ["ref", "option", "items", "rows", "overrides", "shown"],
  "composer.update": ["thread", "text", "files"],
  "presence.update": ["thread", "caret", "selection", "active", "device_kind", "view", "executor"],
  "focus.set": ["focused", "context_ref", "page_state"],
  "tab.close": ["tab_id"],
  "sandbox.result": ["call", "result", "error"],
  "sandbox.tool_call": ["call", "seq", "name", "args"],
  "sandbox.wait": ["call", "seq", "choices", "timeout_ms"],
  "sandbox.fs": ["req", "workspace", "op", "path", "offset", "limit"],
  "sandbox.fs_answer": ["req", "result", "error"],
  ping: [],
} as const satisfies { [K in CommandType]: readonly (keyof Commands[K])[] };

export const FRAME_FIELDS = {
  "hello.ok": ["tab_id", "device_id", "colour", "focus", "idle_after", "ping_interval", "commands"],
  snapshot: [
    "thread_id",
    "rev",
    "profile",
    "channel",
    "context_ref",
    "model_ref",
    "messages",
    "has_earlier",
    "pending_seqs",
    "run",
    "open_choices",
    "choices",
    "context_choices",
    "composer",
  ],
  "message.appended": ["thread_id", "rev", "message"],
  "run.status": ["thread_id", "rev", "run", "input_seqs"],
  "choice.requested": ["thread_id", "rev", "choice"],
  "choice.resolved": ["thread_id", "rev", "choice"],
  "thread.updated": ["thread_id", "rev", "thread"],
  "message.delta": ["thread_id", "run_id", "index", "offset", "part_kind", "content"],
  "tool.call": ["thread_id", "run_id", "id", "name", "activity"],
  "tool.result": ["thread_id", "run_id", "id", "name", "outcome", "activity"],
  "composer.patch": ["thread_id", "rev", "text", "files", "tab_id"],
  presence: ["tabs"],
  "focus.changed": ["device_id", "context_ref", "tab_id"],
  "threads.changed": ["thread_id"],
  "context.changed": ["context_ref"],
  "choice.updated": ["context_ref", "choice"],
  "sandbox.host": ["role", "kind", "epoch", "served"],
  "sandbox.waiting": ["calls"],
  "sandbox.tool_result": ["call", "seq", "status", "result", "choice", "error"],
  "sandbox.fs_result": ["req", "result", "error"],
  "sandbox.fs_request": ["req", "workspace", "op", "path", "offset", "limit"],
  "workspace.changed": ["workspace", "index_rev", "size_bytes"],
  ack: ["command", "result"],
  error: ["command", "code", "message", "fields", "retryable"],
  pong: [],
} as const satisfies { [K in FrameType]: readonly (keyof Frames[K])[] };

export const TAB_FIELDS = [
  "tab_id",
  "device_id",
  "colour",
  "thread_id",
  "context_ref",
  "focused",
  "caret",
  "selection",
  "device_kind",
  "view",
  "last_active",
  "idle",
] as const satisfies readonly (keyof PresenceTab)[];

export const PART_FIELDS = {
  text: ["text"],
  reasoning: ["text"],
  tool_call: ["id", "name", "args", "activity", "ui_kind"],
  tool_result: ["id", "name", "outcome", "content"],
  file: ["thread_file_id", "media_type", "name", "pages"],
  voice: ["thread_file_id", "media_type", "name", "seconds", "transcript"],
  chart: ["title", "vegalite", "table", "source_refs", "as_of"],
  preview: ["workspace", "path"],
  sandbox_cell: ["language", "summary", "calls", "approvals", "duration_ms"],
} as const satisfies {
  [K in WirePart["kind"]]: readonly Exclude<keyof Extract<WirePart, { kind: K }>, "kind">[];
};

export const CLOSE_CODES = {
  unauthenticated: 4401,
  forbidden: 4403,
  tab_closed: 4410,
  unavailable: 4503,
} as const;
export type CloseCodeName = keyof typeof CLOSE_CODES;

/** What a close's reason may say beyond its code. */
export const CLOSE_REASONS = {
  /** With 4403: the user's AI access was just removed; ask for no ticket. */
  access_off: "access_off",
} as const;

export const ERROR_CODES = [
  "hello_required",
  "invalid",
  "review_changes",
  "not_found",
  "forbidden",
  "budget_exceeded",
  "unavailable",
  "internal",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const HELLO_TIMEOUT_SECONDS = 5;
export const MAX_SUBSCRIPTIONS = 10;
